<template>
    <b-modal
        id="dlgInviteMember"
        ref="inviteMemberDialog"
        :title="$t('project.settings.participants.invite')"
        :ok-title="$t('project.settings.participants.inviteDlg.send')"
        ok-variant="outline-info"
        :cancel-title="$t('general.cancel')"
        cancel-variant="outline-secondary"
        header-bg-variant="secondary"
        header-text-variant="white"
        centered
        no-fade
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        @ok.prevent="onOkClicked"
        @cancel="onCancelClicked">
        <b-form novalidate>
            <b-form-group>
                <label>{{ $t('project.settings.participants.inviteDlg.email') }} *</label>
                <vue-tags-input
                    ref="emailInputTag"
                    v-model="emailTag"
                    :tags="emailTags"
                    :separators="[';', ',', ' ']"
                    :add-on-key="[13, 32]"
                    :placeholder="$t('project.settings.participants.inviteDlg.emailPlaceholder')"
                    @tags-changed="setEmailTags"
                    @blur="triggerClickOnBlur"
                />
                <span v-show="inviteFields.emails.length === 0" style="color: red;" class="small">{{ $t('project.settings.participants.inviteDlg.validation.email') }}</span>
            </b-form-group>
            <b-form-group v-if="inputLimits">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('project.settings.participants.inviteDlg.message') }}:</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{inviteFields.message.length}} / {{inputLimits.inviteMessage}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-textarea
                    v-model="inviteFields.message"
                    :rows="3"
                    :maxlength="inputLimits.inviteMessage"
                    no-resize>
                </b-form-textarea>
            </b-form-group>
            <b-form-group>
                <label>{{ $t('project.settings.participants.inviteDlg.valid') }} *</label>
                <b-form-input
                    name='txtValidity'
                    v-validate="'required|numeric|min_value:1|max_value:90'"
                    type="number"
                    v-model="inviteFields.validity"
                    min="1"
                    max="90"
                    :state="!veeErrors.has('txtValidity')">
                </b-form-input>
                <span v-show="veeErrors.has('txtValidity')" style="color: red;" class="small">{{ $t('project.settings.participants.inviteDlg.validation.period') }}</span>
            </b-form-group>
            <b-form-group>
                <label>{{ $t('project.settings.participants.rolesLabel') }} *</label>
                <b-form-select
                    name="roleList"
                    v-model="inviteFields.membership"
                    :options="getRoles"
                    v-validate="'required'"
                    :state="!veeErrors.has('roleList')">
                </b-form-select>
                <span v-show="veeErrors.has('roleList')" style="color: red;" class="small">{{ $t('project.settings.participants.inviteDlg.validation.role') }}</span>
            </b-form-group>
            <b-form-group class="mb-0">
                <b-form-checkbox v-model="hasBcc" @change="copyOwnerEmail">
                    <span class="line-h-25" v-text="$t('project.settings.participants.inviteDlg.bcc')"></span>
                </b-form-checkbox>
                <vue-tags-input
                    ref="bccInputTag"
                    v-show="hasBcc"
                    v-model="bccTag"
                    :tags="bccTags"
                    :separators="[';', ',', ' ']"
                    :add-on-key="[13, 32]"
                    :placeholder="$t('project.settings.participants.inviteDlg.emailPlaceholder')"
                    @tags-changed="setBccTags"
                    @blur="triggerClickOnBlur"
                />
            </b-form-group>
        </b-form>
    </b-modal>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import i18n from '@/i18n.bootstrap';

import VueTagsInput from '@johmun/vue-tags-input';

export default {
    name: 'invitation-dialog',

    props: ['successCallback'],

    components: {
        VueTagsInput
    },

    data() {
        return {
            hasBcc: false,
            bccTag: '',
            emailTag: '',
            bccTags: [],
            emailTags: [],
            emailRegExp: new RegExp(/.+@.+/),
            tagValidation: [{
                classes: 'is-valid-email',
                rule: /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/,
                disableAdd: true
              }],

            inviteFields: {
                bcc: [],
                emails: [],
                message: '',
                validity: null,
                membership: null
            }
        }
    },

    mounted() {
        this.inviteFields = {
            bcc: [],
            emails: [],
            message: '',
            validity: null,
            membership: null
        };
    },

    computed: {
        getRoles() {
          const roles = this.$store.getters['system/roles'];
          return roles? roles.map(role => ({
                text: role.permanent? i18n.t('project.settings.participants.roles.' + role.name.toLowerCase()) : role.name,
                value: role.id
              })) : [];
        },
        ...mapState('system', ['inputLimits']),
        ...mapGetters({
            userData: 'userData/userData',
            projectData: 'projects/projectData',
        })
    },

    methods: {
        setBccTags(newTags) {
            this.inviteFields.bcc = newTags.map(tag => tag.text)
        },

        setEmailTags(newTags) {
            this.inviteFields.emails = newTags.map(tag => tag.text);
        },

        triggerClickOnBlur () {
            this.$root.$el.click();
        },

        onCancelClicked() {
            this.clearForm();
        },

        onOkClicked() {
            this.$validator.validateAll().then(result => {
                if (result && this.inviteFields.emails.length !== 0 ) {
                    this.$store.dispatch('system/inviteParticipants', {
                        bcc: this.inviteFields.bcc,
                        emails: this.inviteFields.emails,
                        message: this.inviteFields.message,
                        roleId: this.inviteFields.membership,
                        project: this.projectData.id,
                        expiresInDays: this.inviteFields.validity
                    })
                    .then(() => {
                        this.clearForm();
                        this.$root.$emit('bv::hide::modal', 'dlgInviteMember');
                        if (this.successCallback) {
                            this.successCallback();
                        }
                    });
                }
            });
        },

        copyOwnerEmail(bccEnabled) {
            if(bccEnabled) {
                this.inviteFields.bcc.push(this.userData.email);
                this.bccTags.push({ text: this.userData.email});
            } else {
                this.inviteFields.bcc = [];
                this.bccTags = [];
            }
        },

        clearForm() {
            this.hasBcc = false,
            this.emailTag = '',
            this.inviteFields = {
                bcc: [],
                emails: [],
                message: '',
                validity: null,
                membership: null
            };
            this.bccTags = [];
            this.emailTags = [];
            this.$validator.reset();
        }
    }
}
</script>

<style lang="scss">
.vue-tags-input {
    max-width: none!important;

    .ti-tag {
        font-size: 13px;
        height: 26px;
        border-radius: 0;
        background-color: #0067a3;
        border-color: #0067a3;
        color: #eeeeee;

        .ti-icon-close {
            color: #dfdfdf;
            margin-left: 3px;
            font-weight: 400;
        }
    }
}
</style>
